import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del } from '@/services/ApiService';
import {
    GET_SHOP_HELP,
    GET_SHOP_HELP_ADMIN,
    UPDATE_SHOP_HELP,
    RESET_SHOP_HELP,
    GET_FAQ_SHOP,
    CREATE_FAQ_SHOP,
    GET_FAQ_SHOP_BY_ID,
    UPDATE_FAQ_SHOP,
    DELETE_FAQ_SHOP
} from '@/services/ApiRoutes';

export interface HelpSupportChannel {
    _id?: string;
    id?: string;
    channelType: 'email' | 'phone' | 'chat' | 'banner';
    title: string;
    subtitle: string;
    value?: string;
    actionText: string;
    actionLink: string;
    icon?: string;
    isActive: boolean;
}

export interface HelpFaqItem {
    _id?: string;
    id?: string;
    question: string;
    answer: string;
    category: string;
    isActive: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface ShopHelpData {
    _id?: string;
    id?: string;
    badge: string;
    title: string;
    subtitle: string;
    searchPlaceholder: string;
    categories: string[];
    supportChannels: HelpSupportChannel[];
    lastUpdated?: string;
}

interface ShopHelpState {
    content: ShopHelpData | null;
    faqs: HelpFaqItem[];
    loading: boolean;
    error: string | null;
    saving: boolean;
}

const initialState: ShopHelpState = {
    content: null,
    faqs: [],
    loading: false,
    error: null,
    saving: false,
};

export const fetchShopHelp = createAsyncThunk(
    'shopHelp/fetchPublic',
    async (_, { rejectWithValue }) => {
        try {
            const response = await get(GET_SHOP_HELP);
            const data = response.data?.data || response.data || {};
            return {
                content: data.content || null,
                faqs: data.faqs || [],
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch Shop Help Center');
        }
    }
);

export const fetchAdminShopHelp = createAsyncThunk(
    'shopHelp/fetchAdmin',
    async (_, { rejectWithValue }) => {
        try {
            const response = await get(GET_SHOP_HELP_ADMIN);
            const data = response.data?.data || response.data || {};
            return {
                content: data.content || null,
                faqs: data.faqs || [],
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch Admin Help Center data');
        }
    }
);

export const updateShopHelp = createAsyncThunk(
    'shopHelp/updateContent',
    async (payload: Partial<ShopHelpData>, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_SHOP_HELP, payload);
            return response.data?.data || response.data || response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to save Help Center content');
        }
    }
);

export const createHelpFaq = createAsyncThunk(
    'shopHelp/createFaq',
    async (payload: Partial<HelpFaqItem>, { rejectWithValue }) => {
        try {
            const response = await post(CREATE_FAQ_SHOP, payload);
            return response.data?.faq || response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create FAQ');
        }
    }
);

export const updateHelpFaq = createAsyncThunk(
    'shopHelp/updateFaq',
    async ({ id, data }: { id: string; data: Partial<HelpFaqItem> }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_FAQ_SHOP(id), data);
            return response.data?.faq || response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update FAQ');
        }
    }
);

export const deleteHelpFaq = createAsyncThunk(
    'shopHelp/deleteFaq',
    async (id: string, { rejectWithValue }) => {
        try {
            await del(DELETE_FAQ_SHOP(id));
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete FAQ');
        }
    }
);

const shopHelpSlice = createSlice({
    name: 'shopHelp',
    initialState,
    reducers: {
        clearHelpError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch Public Help
        builder.addCase(fetchShopHelp.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchShopHelp.fulfilled, (state, action) => {
            state.loading = false;
            state.content = action.payload.content;
            state.faqs = action.payload.faqs;
        });
        builder.addCase(fetchShopHelp.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Fetch Admin Help
        builder.addCase(fetchAdminShopHelp.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchAdminShopHelp.fulfilled, (state, action) => {
            state.loading = false;
            state.content = action.payload.content;
            state.faqs = action.payload.faqs;
        });
        builder.addCase(fetchAdminShopHelp.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Update Content
        builder.addCase(updateShopHelp.pending, (state) => {
            state.saving = true;
            state.error = null;
        });
        builder.addCase(updateShopHelp.fulfilled, (state, action) => {
            state.saving = false;
            state.content = action.payload;
        });
        builder.addCase(updateShopHelp.rejected, (state, action) => {
            state.saving = false;
            state.error = action.payload as string;
        });

        // Create FAQ
        builder.addCase(createHelpFaq.fulfilled, (state, action) => {
            if (action.payload) {
                state.faqs.unshift(action.payload);
            }
        });

        // Update FAQ
        builder.addCase(updateHelpFaq.fulfilled, (state, action) => {
            const updated = action.payload;
            if (updated) {
                const idx = state.faqs.findIndex((f) => (f._id || f.id) === (updated._id || updated.id));
                if (idx !== -1) {
                    state.faqs[idx] = updated;
                }
            }
        });

        // Delete FAQ
        builder.addCase(deleteHelpFaq.fulfilled, (state, action) => {
            state.faqs = state.faqs.filter((f) => (f._id || f.id) !== action.payload);
        });
    },
});

export const { clearHelpError } = shopHelpSlice.actions;
export default shopHelpSlice.reducer;
